/**
 * In-stock products first on collection pages, without the filter ever reaching the URL.
 *
 * 88% of this catalogue is sold (2051 of 2327 active products), so a collection opens on a
 * wall of sold watches. Shopify cannot help here: there is no "in stock first" sort, the
 * brand collections are automated so they cannot be hand-ordered, and Liquid cannot scope
 * collection.products by availability unless the filter is in the URL.
 *
 * Manual ordering plus a cron was the other option and was rejected on purpose: it is a
 * snapshot, so it is wrong from the moment a watch sells until the job next runs. Reading
 * the availability filter at request time is correct by construction and has nothing to
 * keep in sync.
 *
 * ## How it works
 *
 * The server renders the collection exactly as before: every product, sold ones included,
 * with its real pagination links. That is what a crawler gets, so the crawl path and the
 * indexed page are untouched and there are no competing canonicals. This matters, because
 * Shopify strips filter params from the canonical but keeps `page`, so two filtered phases
 * would otherwise both claim `/collections/x?page=2` with different products in them.
 *
 * For a visitor, this element then:
 *
 *   1. Hides the sold cards already in page 1's HTML. They are in the DOM, so this is
 *      instant, with no fetch and no flash.
 *   2. Walks `filter.v.availability=1` page by page, appending the in-stock products.
 *   3. When that runs out, reveals the sold cards it hid in step 1, moves them to the end,
 *      and walks `filter.v.availability=0` for the rest.
 *
 * The address bar never changes. Everything is fetched through the theme's own
 * `?section_id=` endpoint, the same one dynamic-product-search already uses.
 *
 * Adapted from the Paddle Pro Shop theme (~/Docs/shopify-themes/enterprise-padel-pro,
 * assets/custom-pagination.js). Two deliberate differences: that one starts the filtered
 * phase at page 2, inheriting the page number from the unfiltered link, which here would
 * skip a whole page of in-stock results, roughly 24 of only 139 in-stock Rolexes. So this
 * starts at page 1 and dedupes by product id instead. It also leaves page 1 unfiltered,
 * which at 88% sold would still open on mostly sold watches, hence step 1.
 *
 * Disabled whenever a filter or a sort is active, so the shopper's own choice always wins.
 */

const SELECTORS = {
  grid: '.collection__cards',
  item: '.o-layout__item',
  card: '[data-product-id]',
  soldOut: '.product-card--sold-out',
  reserved: '.product-card--reserved',
  animated: '.product-card-top',
  pagination: '.collection-pagination'
};

// A collection can genuinely run to 90 pages here. This only exists so a malformed
// response can never spin the observer forever.
const MAX_PAGES = 200;

class StockFirstCollection extends HTMLElement {
  connectedCallback() {
    if (this.dataset.enabled !== 'true') return;

    this.section = this.closest('.section');
    if (!this.section) return;

    this.grid = this.section.querySelector(SELECTORS.grid);
    if (!this.grid) return;

    this.collectionUrl = this.dataset.collectionUrl;
    this.sectionId = this.dataset.sectionId;
    this.pagination = this.section.querySelector(SELECTORS.pagination);

    this.phase = 'in-stock';
    this.page = 1;
    this.isFetching = false;
    this.finished = false;
    this.seen = new Set();
    this.deferred = [];

    this.#collectExisting();
    this.#deferSoldOut();

    // The pagination links stay in the DOM for crawlers and for anyone without JS; they
    // are just not the way a visitor moves through the list any more.
    if (this.pagination) this.pagination.classList.add('collection-pagination--js-hidden');

    this.#observe();
  }

  disconnectedCallback() {
    this.#stopObserving();
    if (this.controller) this.controller.abort();
  }

  #collectExisting() {
    this.grid.querySelectorAll(SELECTORS.card).forEach((card) => {
      const id = card.dataset.productId;
      if (id) this.seen.add(id);
    });
  }

  /**
   * Hide the sold cards that came down with page 1, remembering them so they can be put
   * back at the end of the list once the in-stock products are exhausted.
   */
  #deferSoldOut() {
    this.grid.querySelectorAll(SELECTORS.item).forEach((item) => {
      // A reserved watch reads as unavailable once its stock is moved out of Available,
      // but it is a live piece, not archive, so it keeps the place the server gave it.
      if (item.querySelector(SELECTORS.reserved)) return;
      if (item.querySelector(SELECTORS.soldOut)) {
        item.classList.add('is-stock-deferred');
        this.deferred.push(item);
      }
    });
  }

  #revealDeferred() {
    const restored = this.deferred;
    restored.forEach((item) => {
      item.classList.remove('is-stock-deferred');
      this.grid.appendChild(item);
    });
    this.deferred = [];
    this.#animate(restored);
  }

  /**
   * Hand new cards to ScrollReveal.
   *
   * The theme hides .product-card-top until ScrollReveal reveals it, and ScrollReveal only
   * knows about elements present when it ran. Cards appended later therefore load their
   * images and then sit at visibility:hidden forever. The theme's own AJAX path in
   * theme.js does the same thing after re-rendering a section; this mirrors it.
   */
  #animate(items) {
    if (!items || items.length === 0) return;
    if (document.body.dataset.animLoad !== 'true' || !window.sr) return;

    const targets = [];
    items.forEach((item) => {
      const el = item.querySelector(SELECTORS.animated);
      if (el) targets.push(el);
    });
    if (targets.length === 0) return;

    try {
      window.sr.reveal(targets, {
        interval: (window.theme && window.theme.intervalValue) || 0,
        delay: 0
      });
    } catch (error) {
      // Never let an animation problem stop products appearing.
      targets.forEach((el) => {
        el.style.visibility = 'visible';
        el.style.opacity = '1';
      });
    }
  }

  #observe() {
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.#loadMore();
            // Keep going while the sentinel stays in view. Appending a page usually does
            // not push it back out, and IntersectionObserver only reports changes, so
            // without this the walk loads one page and then stalls.
            if (!this.timer) {
              this.timer = window.setInterval(() => this.#loadMore(), 600);
            }
          } else {
            this.#stopTimer();
          }
        });
      },
      { rootMargin: `${window.innerHeight * 1.5}px 0px` }
    );
    this.observer.observe(this);
  }

  #stopTimer() {
    if (this.timer) {
      window.clearInterval(this.timer);
      this.timer = null;
    }
  }

  #stopObserving() {
    this.#stopTimer();
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  #url() {
    const availability = this.phase === 'in-stock' ? '1' : '0';
    const params = new URLSearchParams({
      'filter.v.availability': availability,
      page: String(this.page),
      section_id: this.sectionId
    });
    return `${this.collectionUrl}?${params.toString()}`;
  }

  async #loadMore() {
    if (this.isFetching || this.finished) return;
    if (this.page > MAX_PAGES) return this.#finish();

    this.isFetching = true;
    this.setAttribute('loading', '');
    this.controller = new AbortController();

    try {
      const response = await fetch(this.#url(), { signal: this.controller.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');

      // Only items that actually hold a product card. A page past the end of a phase is not
      // empty: the section renders its "no matches" message inside an .o-layout__item of its
      // own, so counting raw items here would never see the end and would walk to MAX_PAGES.
      const incoming = Array.from(doc.querySelectorAll(`${SELECTORS.grid} ${SELECTORS.item}`)).filter(
        (item) => item.querySelector(SELECTORS.card)
      );

      // No products means this phase is done, regardless of how many were duplicates.
      if (incoming.length === 0) return this.#advancePhase();

      let added = 0;
      const appended = [];
      incoming.forEach((item) => {
        const card = item.querySelector(SELECTORS.card);
        const id = card && card.dataset.productId;
        if (!id || this.seen.has(id)) return;
        this.seen.add(id);
        this.grid.appendChild(item);
        appended.push(item);
        added += 1;
      });

      this.#animate(appended);

      this.page += 1;

      // A page that was entirely duplicates still counts as progress, so keep walking.
      // Only an empty response ends a phase.
      if (added === 0 && incoming.length > 0) this.#loadMoreSoon();
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.warn('stock-first-collection: could not load more products', error);
        this.#finish();
      }
    } finally {
      this.isFetching = false;
      this.controller = null;
      this.removeAttribute('loading');
    }
  }

  // Deliberately out of band: lets the observer settle before the next request so a run of
  // duplicate-only pages does not fire them all in the same tick.
  #loadMoreSoon() {
    window.setTimeout(() => this.#loadMore(), 0);
  }

  #advancePhase() {
    if (this.phase === 'in-stock') {
      this.phase = 'sold';
      this.page = 1;
      this.#revealDeferred();
      this.#loadMoreSoon();
      return;
    }
    this.#finish();
  }

  #finish() {
    this.finished = true;
    this.#revealDeferred();
    this.#stopObserving();
    this.removeAttribute('loading');
  }
}

if (!customElements.get('stock-first-collection')) {
  customElements.define('stock-first-collection', StockFirstCollection);
}
